import { DEFAULT_ROSTER_PAGE_SIZE } from '~/modules/artist-roster/constants';
import { downloadFile } from '~/modules/artist-roster/lib/downloadFile';
import { getArtistEntityType } from '~/modules/artist-roster/lib/utils/getArtistEntityType';

import {
  DATA_TOOLS_ORDER,
  IDENTIFIER_TYPES,
  SOCIALS_ORDER,
} from '../lib/constants';
import type {
  ArtistContact,
  ArtistContactInfoResponse,
  ArtistContacts,
  ArtistDetails,
  ArtistDetailsDataResponse,
  ArtistSearchDataResponse,
  ArtistType,
  Identifier,
  IdentifierLink,
  SocialIdentifierLink,
} from '../types';

export interface ArtistSearchResponse {
  items: ArtistType[];
  pagination: { offset: number; total: number };
}

const REPORTS_IDENTIFIERS: Record<
  string,
  { displayName: string; url: (value: string) => string }
> = {
  [IDENTIFIER_TYPES.SODATONE]: {
    displayName: 'Sodatone',
    url: (value) => `https://app.sodatone.com/artists/${value}`,
  },
};

const SOCIAL_IDENTIFIERS: Record<
  string,
  { displayName: string; url: (value: string) => string }
> = {
  [IDENTIFIER_TYPES.SPOTIFY]: {
    displayName: 'Spotify',
    url: (value) => `https://open.spotify.com/artist/${value}`,
  },

  [IDENTIFIER_TYPES.INSTAGRAM]: {
    displayName: 'Instagram',
    url: (value) => `https://www.instagram.com/${value}`,
  },

  [IDENTIFIER_TYPES.YOUTUBE]: {
    displayName: 'YouTube',
    url: (value) => `https://www.youtube.com/channel/${value}`,
  },

  [IDENTIFIER_TYPES.TIKTOK]: {
    displayName: 'TikTok',
    url: (value) => `https://www.tiktok.com/@${value}`,
  },
};

const formatIdentifier = (
  identifier: Identifier,
  filter: Record<
    string,
    { displayName: string; url: (value: string) => string }
  >,
) => {
  const formatUrl = filter[identifier.identifier_type.toLowerCase()];

  return formatUrl
    ? {
        name: identifier.identifier_type.toLowerCase(),
        url: identifier.attributes?.url
          ? identifier.attributes?.url
          : formatUrl.url(identifier.value),
        displayName: formatUrl.displayName,
        followerCount: identifier.attributes?.follower_count,
      }
    : null;
};

const getArtistUAID = (identifiers: Identifier[]): number | null => {
  const uaidIdentifiers = identifiers.filter(
    (idn) => idn.identifier_type === 'UAID',
  );

  if (!uaidIdentifiers.length) {
    return null;
  }

  const uaid = Number(uaidIdentifiers[0].value);

  return Number.isNaN(uaid) ? null : uaid;
};

const formatArtistData = (
  artist: ArtistDetailsDataResponse,
): ArtistType | null => {
  const names = artist.names.filter(
    (n) => n.name_type === 'PRIMARILY_KNOWN_AS',
  );

  if (!names.length) {
    return null;
  }

  const name = names[0];

  const uaid = getArtistUAID(name.identifiers);

  return {
    partyId: artist.party_id,
    uaid,
    name: name.default_name,
    entityType: getArtistEntityType(artist),
    labels: artist.labels
      .map((label) => ({
        labelId: label.label_id,
        name: label.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    image: artist.image?.sodatone,
    isCrossover: name.is_crossover_artist,
    reports: [
      ...(name.identifiers
        .map((identifier) => formatIdentifier(identifier, REPORTS_IDENTIFIERS))
        .filter(Boolean) as IdentifierLink[]),
      ...(uaid
        ? [
            {
              name: IDENTIFIER_TYPES.ARTIST_PACK,
              url: `https://audience-insights.onrender.com/decks/d-0/slides/deckTitle?a=${uaid}`,
              displayName: 'Artist Pack',
            },
            {
              name: IDENTIFIER_TYPES.OPUS,
              url: `https://opus.wmg.com/entity/artist/GRP-${uaid}`,
              displayName: 'Opus',
            },
          ]
        : []),
    ].sort(
      (a, b) =>
        DATA_TOOLS_ORDER.indexOf(a.displayName ?? a.name) -
        DATA_TOOLS_ORDER.indexOf(b.displayName ?? b.name),
    ),
    socials: name.identifiers
      .map((identifier) => formatIdentifier(identifier, SOCIAL_IDENTIFIERS))
      .filter((identifier) => !!identifier)
      .sort(
        (a, b) => SOCIALS_ORDER.indexOf(a.name) - SOCIALS_ORDER.indexOf(b.name),
      ) as SocialIdentifierLink[],
  };
};

const formatArtistDetails = (
  artist: ArtistDetailsDataResponse,
  countryCode: string | null,
): ArtistDetails | undefined => {
  const artistData = formatArtistData(artist);

  return artistData
    ? {
        ...artistData,
        countryOfOriginISO: countryCode ?? undefined,
      }
    : undefined;
};

const formatArtistSearchData = (
  data: ArtistSearchDataResponse,
): ArtistSearchResponse => {
  return {
    items: data.items.map(formatArtistData).filter((a): a is ArtistType => !!a),
    pagination: {
      offset: data.pagination_offset.offset,
      total: data.pagination_offset.total,
    },
  };
};

export const getArtists = async ({
  search = '',
  limit = DEFAULT_ROSTER_PAGE_SIZE,
  offset = 0,
  labels = [],
  sortBy = '+NAME',
  isCrossover = false,
  isOnRoster = undefined,
  signal,
}: {
  search?: string;
  limit?: number;
  offset?: number;
  labels?: number[];
  isCrossover?: boolean;
  isOnRoster?: boolean;
  sortBy?: string;
  signal?: AbortSignal;
}) => {
  try {
    const params = new URLSearchParams();

    if (search) {
      params.set('name_search_term', search);

      // @TODO WMG1-932 - Removing for now until the back end can debug what's going on.
      // params.set('name_search_methods', '[FUZZY]');
    }

    params.set('limit', `${limit}`);

    if (offset) {
      params.set('offset', `${offset}`);
    }

    params.set('label_ids', `[${labels.join(',')}]`);

    if (sortBy) {
      params.set('sort_by', sortBy);
    }

    if (isCrossover) {
      params.set('crossover_artist', `${isCrossover}`);
    }

    if (isOnRoster !== undefined) {
      params.set('on_roster', `${isOnRoster}`);
    }

    const response = await fetch(
      `/api/music_api/v1/extended-parties?${params.toString()}`,
      // Allows the caller to abort the existing query if it's in flight.
      { signal },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch artists: ${response.statusText}`);
    }

    const data: ArtistSearchDataResponse = await response.json();

    return formatArtistSearchData(data);
  } catch (error) {
    if (
      (error as { message: string }).message ===
      'signal is aborted without reason'
    ) {
      return { items: [], pagination: { offset: 0, total: 0 } };
    }

    console.error('Error fetching artists:', error);

    throw error;
  }
};

export const exportArtistsCSV = async ({
  search = '',
  labels = [],
  isCrossover = false,
  isOnRoster = undefined,
  sortBy = '+NAME',
  withContactInfo = false,
  withAiConsent = false,
  signal,
}: {
  search?: string;
  labels?: number[];
  isCrossover?: boolean;
  isOnRoster?: boolean;
  sortBy?: string;
  withContactInfo?: boolean;
  withAiConsent?: boolean;
  signal?: AbortSignal;
}): Promise<boolean> => {
  try {
    const searchParams = new URLSearchParams({
      sort_by: sortBy,
      label_ids: `[${labels.join(',')}]`,
    });

    if (search) {
      searchParams.append('name_search_term', search);

      // @TODO WMG1-932 - Removing for now until the back end can debug what's going on.
      // searchParams.set('name_search_methods', '[FUZZY]');
    }

    if (isCrossover) {
      searchParams.append('crossover_artist', `${isCrossover}`);
    }

    if (isOnRoster !== undefined) {
      searchParams.append('on_roster', `${isOnRoster}`);
    }

    if (withContactInfo) {
      searchParams.append('contact_info', 'true');
    }

    if (withAiConsent) {
      searchParams.append('ai_consent', 'true');
    }

    const response = await fetch(
      `/api/music_api/v1/extended-parties/csv?${searchParams}`,
      { signal },
    );

    if (!response.ok || !response.blob) {
      throw new Error(`Failed to export artists: ${response.statusText}`);
    }

    const fileName = `artists-${new Date().toISOString()}${
      search ? '-' + search.replace(/[^a-z0-9]/gi, '_') : ''
    }.csv`;

    await downloadFile({ response, fileName });

    return true;
  } catch (error) {
    console.error('Error exporting artists:', error);

    throw error;
  }
};

const formatArtistContactInfo = ({
  items,
}: ArtistContactInfoResponse): ArtistContacts => {
  const artistContacts: ArtistContact[] = [];
  const songwriterContacts: ArtistContact[] = [];

  items.forEach((item) => {
    const contact = {
      id: item.contact_info_id,
      firstName: item.first_name,
      lastName: item.last_name,
      email: item.email,
      phone: item.phone_number,
      title: item.contact_type,
    };

    if (item.category === 'ARTIST') {
      artistContacts.push(contact);
    } else if (item.category === 'SONGWRITER') {
      songwriterContacts.push(contact);
    }
  });

  return {
    artistContacts,
    songwriterContacts: songwriterContacts.length
      ? songwriterContacts
      : undefined,
  };
};

export const getArtistContactInfo = async ({
  partyId,
}: {
  partyId?: number;
}) => {
  if (!partyId) {
    throw new Error('Party ID is required');
  }

  try {
    const response = await fetch(
      `/api/music_api/v1/contact-info?party_id=${partyId}`,
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch artist contact info: ${response.statusText}`,
      );
    }

    const data: ArtistContactInfoResponse = await response.json();

    return formatArtistContactInfo(data);
  } catch (error) {
    console.error(`Error fetching contact info for artist ${partyId}:`, error);

    throw error;
  }
};

export const getArtistCountryCode = async ({
  countryId,
}: {
  countryId?: string;
}) => {
  if (!countryId) {
    return null;
  }

  try {
    const response = await fetch(
      `/api/music_api/v1/countries?country_id=${countryId}`,
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch artist country code: ${response.statusText}`,
      );
    }

    const data = await response.json();

    return data.items?.length ? data.items[0].iso_code : null;
  } catch (error) {
    console.error('Error fetching artist country code:', error);

    throw error;
  }
};

export const getArtistDetail = async ({ partyId }: { partyId?: string }) => {
  if (!partyId) {
    throw new Error('Party ID is required');
  }

  try {
    const response = await fetch(
      `/api/music_api/v1/extended-parties/${partyId}`,
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch artists: ${response.statusText}`);
    }

    const data: ArtistDetailsDataResponse = await response.json();
    const countryCode = await getArtistCountryCode({
      countryId: data.country_of_origin,
    });

    return formatArtistDetails(data, countryCode);
  } catch (error) {
    console.error('Error fetching artists:', error);

    throw error;
  }
};

export const getCountryName = (countryCode: string, lang = 'en') => {
  const regionNames = new Intl.DisplayNames([lang], { type: 'region' });

  return regionNames.of(countryCode);
};
